import type { PropertyValues, TemplateResult } from 'lit';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from 'src/i18n';
import { PageContent } from 'src/pages/page-content';
import 'src/library/promo/d-promo-section';
import 'src/library/fields/d-expansion';
import 'src/library/fields/d-spinner-robot';
import 'src/library/lists/d-list-section';
import 'src/library/lists/d-list-section-item';
import 'src/library/elements/d-action';
import 'src/pages/substances-page/d-substances-table';
import type { SubstancesPageViewModel } from 'src/pages/substances-page/substances-page-view-model';
import { cloneDeep } from 'lodash';
import type { PageLink, PageLinks } from 'src/pages/overview-page/overview-page-view-model';
import { features } from '../../store/selectors/features/features';
import { registerItemsPromptStyles } from 'src/library/register-items-prompt-styles';
import { commonFieldsByCode } from 'src/store/fields';
import type { TableDefinition } from '../../library/abstracts/table-definition';
import type { ItemTableColumn } from '../../library/abstracts/item-table-column';
import {
  getAllUploadsForDisplay,
  UploadQueueChangedEvent,
  removeExpiredDuplicates,
  removeExpiredFailedUploads,
} from '../../services/substance-upload-queue';
import type { QueuedUpload } from '../../services/substance-upload-types';
import type { TableItemsSubstance } from './table-items-substance';
import { LocalDateTime } from 'src/utilities/local-date-time.js';

/**
 *
 * STATUS OK
 */
@customElement('d-substances-page-content')
export class DSubstancesPageContent extends PageContent<SubstancesPageViewModel> {
  static readonly styles = [
    ...super.styles,
    registerItemsPromptStyles,
    css`
      d-expansion > div {
        position: relative;
      }

      .cancel-upload {
        position: relative;
        padding-top: 20px;
        text-align: right;
        z-index: 1;
      }

      .register-items-prompt {
        display: none;
      }

      .ready .register-items-prompt {
        display: block;
      }

      #fileInput {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        opacity: 0;
        cursor: pointer;
      }

      .pendingAlert,
      .unreadableAlert {
        display: none;
      }

      .substanceAlert {
        display: flex;
        align-items: center;
        width: 100%;
        margin: 8px 0;
      }

      #flexWrapper > d-ghs-hazard-label {
        flex: none;
        border-bottom: 1px solid var(--borderColor);
        margin-bottom: -1px;
      }

      #flexWrapper > d-ghs-hazard-label > .sectionElementInner {
        border-left: none;
      }

      .ghsLabel {
        width: 100px;
        padding-top: 104px;
        background-size: 95px 95px;
        background-position: 50% 0;
        background-repeat: no-repeat;
        text-align: center;
        hyphens: auto;
      }

      @media only screen and (max-width: 600px) {
        .ghsLabel {
          width: 95px;
          font-size: 14px;
        }
      }

      .maxWidth600 .ghsLabel {
        width: 95px;
        font-size: 14px;
      }

      .GHS01 {
        background-image: url(/images/ghs/ghs-01-explosive.svg);
      }

      .GHS02 {
        background-image: url(/images/ghs/ghs-02-flammable.svg);
      }

      .GHS03 {
        background-image: url(/images/ghs/ghs-03-oxidizing.svg);
      }

      .GHS04 {
        background-image: url(/images/ghs/ghs-04-compressed-gas.svg);
      }

      .GHS05 {
        background-image: url(/images/ghs/ghs-05-corrosive.svg);
      }

      .GHS06 {
        background-image: url(/images/ghs/ghs-06-toxic.svg);
      }

      .GHS07 {
        background-image: url(/images/ghs/ghs-07-harmful.svg);
      }

      .GHS08 {
        background-image: url(/images/ghs/ghs-08-health-hazard.svg);
      }

      .GHS09 {
        background-image: url(/images/ghs/ghs-09-enviromental-hazard.svg);
      }

      d-substance-view .listContent d-list-section-item {
        line-height: 150%;
      }

      .sticker .itemName:before,
      .groupRow.riskLevel .field-riskGroupName > span:before {
        content: '';
        margin-right: 8px;
        padding: 4px 5px 4px 7px;
        border-radius: 3px;
        color: white;
        font-family: var(--small);
        text-transform: uppercase;
        font-size: 10px;
        font-weight: 500;
        letter-spacing: 2px;
        opacity: 1;
      }

      .sticker .itemName:before {
        display: inline;
        position: relative;
        top: -2px;
      }

      .sticker.suggestion .itemName:before {
        content: 'Forslag';
        background: hsl(0, 0%, 75%);
      }

      .wide .groupRow.riskLevel .field-riskGroupName {
        display: flex;
        flex-direction: row-reverse;
      }

      .groupRow.riskLevel .field-riskGroupName > span {
        display: block;
        width: 20px;
        height: 20px;
        border-radius: 50%;
      }

      .narrow .groupRow.riskLevel .field-riskGroupName > span {
        position: relative;
        top: 6px;
        margin-top: -4px;
      }

      .columnLabels .field-riskGroupName {
        text-align: right;
      }

      .groupRow.riskLevel .field-riskGroupName > span {
        font-size: 0;
      }

      .groupRow.riskLevel .field-riskGroupName > span:before {
        display: inline;
        position: relative;
        top: 4px;
      }

      .narrow .groupRow.riskLevel .field-riskGroupName > span:before {
        top: -4px;
      }

      .sticker.risk1 .itemName:before {
        content: 'Lav';
      }

      .sticker.risk2 .itemName:before {
        content: 'Moderat';
      }

      .sticker.risk3 .itemName:before {
        content: 'Høy'; /* i18n-exempt: CSS ::before risiko-etikett */
      }

      .sticker.risk4 .itemName:before {
        content: 'Svært høy'; /* i18n-exempt: CSS ::before risiko-etikett */
      }

      .groupRow.riskLevel.risk1 .field-riskGroupName > span:before {
        content: 'L';
      }

      .groupRow.riskLevel.risk2 .field-riskGroupName > span:before {
        content: 'M';
      }

      .groupRow.riskLevel.risk3 .field-riskGroupName > span:before {
        content: 'H';
      }

      .groupRow.riskLevel.risk4 .field-riskGroupName > span:before {
        content: 'S';
      }

      .groupLabel.riskLevel .big #label {
        margin-right: 8px;
        padding: 6px 7px 4px 10px;
        border-radius: 4px;
        color: white;
        font-family: var(--small);
        text-transform: uppercase;
        font-size: 13px;
        font-weight: 500;
        letter-spacing: 2px;
      }

      .narrow .groupLabel.riskLevel .big #label {
        margin-top: -4px;
      }

      .sticker.risk1 .itemName:before,
      .groupRow.riskLevel.risk1 .field-riskGroupName > span:before,
      .groupLabel.riskLevel.risk1 #label {
        background: hsl(106, 87%, 43%);
      }

      .sticker.risk2 .itemName:before,
      .groupRow.riskLevel.risk2 .field-riskGroupName > span:before,
      .groupLabel.riskLevel.risk2 #label {
        background: hsl(62, 57%, 39%);
      }

      .sticker.risk3 .itemName:before,
      .groupRow.riskLevel.risk3 .field-riskGroupName > span:before,
      .groupLabel.riskLevel.risk3 #label {
        background: hsl(31, 93%, 51%);
      }

      .sticker.risk4 .itemName:before,
      .groupRow.riskLevel.risk4 .field-riskGroupName > span:before,
      .groupLabel.riskLevel.risk4 #label {
        background: hsl(0, 80%, 56%);
      }
    `,
  ];

  @property({ type: String })
  progressState = 'ready';
  /**
   * Open the upload user interface. Property instead of state so it can be used in stories.
   */
  @property({ type: Boolean })
  showUploader = false;

  @property({ type: Boolean })
  dropTargetHover = false;
  @property({ type: Boolean, attribute: 'write-access', reflect: true })
  writeAccess = false;
  @state()
  private uploadInProgress = false;
  @state()
  private queuedItems: TableItemsSubstance[] = [];

  private queueChangedHandler = () => this.loadQueuedItems();
  private duplicateCleanupInterval: number | null = null;
  private groupResetScheduled = false;

  substancesTableConfig = {
    groupBy: '',
    groupByOptions: ['riskGroupSort', 'location', 'supplier', 'uploadDateTime'],
    sortBy: 'name',
    sortByOptions: [],
    columns: [
      {
        field: 'classes',
      },
      {
        field: 'uploadDateTime',
        label: t('substances.columnUploadTime'),
        type: 'relativeDateAndTime',
        width: 0,
        groupOrder: 'DESC',
        hideColumn: true,
      },
      {
        field: 'location',
        label: t('substances.columnLocation'),
        width: 0,
        hideColumn: true,
      },
      {
        field: 'supplier',
        label: t('substances.supplierTerm'),
        width: 0,
        hideColumn: true,
      },
      {
        field: 'name',
        label: t('substances.columnName'),
        width: 400,
      },
      {
        field: 'hazardLabels',
        label: '',
        width: 100,
        type: 'icons',
      },
      {
        field: 'applications',
        label: t('substances.columnApplications'),
        width: 600,
      },
      {
        field: 'riskGroupName',
        label: t('substances.columnRisk'),
        width: 100,
      },
      {
        field: 'riskGroupSort',
        label: t('substances.columnRiskAssessment'),
        width: 0,
        viewColumn: 'riskGroupName',
        groupClasses: 'riskGroupClasses',
        hideColumn: true,
      },
    ],
  };
  archivedSubstancesTableConfig = this._archivedSubstancesTableConfig(this.substancesTableConfig);

  get noSubstances() {
    return (
      (!this.pageView?.substances || this.pageView.substances.length === 0) &&
      (!this.pageView?.substancesArchived || this.pageView.substancesArchived.length === 0) &&
      this.queuedItems.length === 0
    );
  }

  _upload() {
    this.showUploader = true;
    this.progressState = 'ready';
    const input = this.shadowRoot?.getElementById('fileInput') as HTMLInputElement | null;
    if (input) {
      input.click();
    }
  }

  _uploadQueued() {
    this.uploadInProgress = true;

    const config = cloneDeep(this.substancesTableConfig);
    config.groupBy = 'uploaded';
    this.substancesTableConfig = config;

    // Close upload UI and return to list
    this.showUploader = false;
    this.progressState = 'ready';
  }

  _cancelUpload() {
    this.showUploader = false;
    setTimeout(() => {
      this.progressState = 'ready';
    }, 1000);
  }

  _empty(list) {
    return list.length === 0;
  }

  _archivedSubstancesTableConfig(substancesTableConfig) {
    const config = cloneDeep(substancesTableConfig);
    config.groupBy = '';
    config.groupByOptions = [];
    return config;
  }

  override connectedCallback() {
    super.connectedCallback();
    window.addEventListener(UploadQueueChangedEvent.TYPE, this.queueChangedHandler);
    // Load initial queue state
    this.loadQueuedItems();
    // Start duplicate cleanup interval
    this.startDuplicateCleanup();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(UploadQueueChangedEvent.TYPE, this.queueChangedHandler);
    this.stopDuplicateCleanup();
  }

  private startDuplicateCleanup() {
    if (this.duplicateCleanupInterval) return;
    // Check for expired duplicates every 5 seconds, and expired failed uploads
    this.duplicateCleanupInterval = window.setInterval(async () => {
      let needsReload = false;

      const removedDuplicates = await removeExpiredDuplicates();
      if (removedDuplicates > 0) {
        console.log(`[SubstancesPage] Removed ${removedDuplicates} expired duplicate entries`);
        needsReload = true;
      }

      // Also clean up failed uploads that are older than 1 hour
      const removedFailed = await removeExpiredFailedUploads();
      if (removedFailed > 0) {
        console.log(`[SubstancesPage] Removed ${removedFailed} expired failed uploads`);
        needsReload = true;
      }

      if (needsReload) {
        await this.loadQueuedItems();
      }
    }, 5000);
  }

  private stopDuplicateCleanup() {
    if (this.duplicateCleanupInterval) {
      clearInterval(this.duplicateCleanupInterval);
      this.duplicateCleanupInterval = null;
    }
  }

  private async loadQueuedItems() {
    try {
      // Get organization ID from pageView if available
      const organizationId = this.pageView?.organizationId?.toString();
      const queuedUploads = await getAllUploadsForDisplay(organizationId);
      this.queuedItems = this.convertQueuedUploadsToTableItems(queuedUploads);

      // If there are queued items, set upload in progress
      if (this.queuedItems.length > 0 && !this.uploadInProgress) {
        this.uploadInProgress = true;
        const config = cloneDeep(this.substancesTableConfig);
        config.groupBy = 'uploaded';
        this.substancesTableConfig = config;
      }
    } catch (error) {
      console.error('[SubstancesPage] Failed to load queued items:', error);
    }
  }

  private convertQueuedUploadsToTableItems(uploads: QueuedUpload[]): TableItemsSubstance[] {
    return uploads.map((upload) => {
      // Remove .pdf extension from filename for display
      const displayName = upload.fileName.replace(/\.pdf$/i, '');

      // Map queue status to table item state
      let state: TableItemsSubstance['state'] = 'queued';
      if (upload.status === 'uploading') {
        state = 'uploading';
      } else if (upload.status === 'failed') {
        state = 'failed';
      } else if (upload.isDuplicate) {
        state = 'duplicate';
      }

      // Use batch upload datetime for both display and sorting
      // batchUploadDateTime is UTC (with Z), convert to local time for display
      const isoDateTime = upload.batchUploadDateTime || new Date(upload.createdAt).toISOString();
      // Parse UTC and convert to local time using Date object
      const uploadedDisplay = LocalDateTime.fromDate(new Date(isoDateTime)).toStringForDisplay();

      return {
        state,
        uploaded: uploadedDisplay,
        uploadedSortValue: isoDateTime,
        name: displayName,
        supplier: '',
        hazardLabels: [],
        applications: '',
        location: '',
        risk: '',
        href: '', // No link for queued items
        uuid: upload.id, // Use queue ID as uuid
        hasDraft: false,
        statusMessage: upload.displayError,
        retryCount: upload.retryCount,
      };
    });
  }

  /**
   * Get substances list merged with queued items
   */
  private get substancesWithQueue(): TableItemsSubstance[] {
    return [...this.queuedItems, ...this.pageView.substances];
  }

  _dragenter(e) {
    e.preventDefault();
    e.stopPropagation();
    this.dropTargetHover = true;
  }

  _dragleave(e) {
    e.preventDefault();
    e.stopPropagation();
    this.dropTargetHover = false;
  }

  _dragover(e) {
    e.preventDefault();
    e.stopPropagation();
    this.dropTargetHover = true;
  }

  _drop(e) {
    e.preventDefault();
    e.stopPropagation();
    this.dropTargetHover = false;
    this._handleDrop(e);
  }

  _registerItemsPromptClasses(dropTargetHover) {
    let classes = 'register-items-prompt substances';
    if (dropTargetHover) {
      classes += ' hover';
    }
    return classes;
  }

  _traverseFileTreePromise(item, path, folder) {
    return new Promise((resolve) => {
      if (item.isFile) {
        item.file((file) => {
          file.filepath = path ?? '' + file.name; //save full path
          folder.push(file);
          resolve(file);
        });
      } else if (item.isDirectory) {
        const dirReader = item.createReader();
        dirReader.readEntries((entries) => {
          const entriesPromises: Promise<unknown>[] = [];
          const subfolder = [];
          folder.push({ name: item.name, subfolder: subfolder });
          for (const entry of entries) {
            entriesPromises.push(this._traverseFileTreePromise(entry, path ?? '' + item.name + '/', subfolder));
          }
          resolve(Promise.all(entriesPromises));
        });
      }
    });
  }

  _getFilesDataTransferItems(dataTransferItems) {
    const files = [];
    return new Promise((resolve) => {
      const entriesPromises: Promise<unknown>[] = [];
      for (const it of dataTransferItems) {
        const item = it.webkitGetAsEntry();
        if (item) {
          entriesPromises.push(this._traverseFileTreePromise(item, '', files));
        }
      }
      Promise.all(entriesPromises).then(() => {
        resolve(files);
      });
    });
  }

  _flattenList(files) {
    let result: any[] = [];
    files.forEach((file) => {
      if (file.subfolder) {
        result = result.concat(this._flattenList(file.subfolder));
      } else if (file.type === 'application/pdf') {
        result.push(file);
      }
    });
    return result;
  }

  _handleDrop(e) {
    this._getFilesDataTransferItems(e.dataTransfer.items).then((files) => {
      const flatList = this._flattenList(files);
      this._handleFiles(flatList);
    });
  }

  _handleFileInputChange(e: InputEvent) {
    e.stopPropagation();
    e.preventDefault();
    const target = e.target as HTMLInputElement;
    const files = target.files;
    this._handleFiles(files);
  }

  _handleFiles(files) {
    if (this.progressState === 'uploading') {
      return;
    }
    this.progressState = 'uploading';
    this.dispatchEvent(
      new CustomEvent<{ files: File[]; callback: () => void }>('upload-substance-files', {
        composed: true,
        bubbles: true,
        detail: {
          files: files,
          callback: () => {
            // Callback returns immediately now
            this._uploadQueued();
          },
        },
      }),
    );
  }

  renderPageLinks(list: PageLinks) {
    return html`
      ${list.items.map((pageLink: PageLink) => {
        return html`<a class="page-link" href="${pageLink.href}">${pageLink.name}</a>`;
      })}
    `;
  }

  renderTableHeader(table: TableDefinition) {
    if (table.field && !table.noHeader) {
      const f = commonFieldsByCode()[table.fieldForLabel];
      if (f) {
        return html`<h2>${f.label}</h2>`;
      }
    }
    return html``;
  }

  renderSubstancesTable(table: TableDefinition) {
    if (table.items) {
      // For substances table, merge with queued items; for archived, use as-is
      const items = table.items === 'substances' ? this.substancesWithQueue : this.pageView[table.items];
      if (items.length) {
        return html` ${this.renderTableHeader(table)}
          <d-substances-table
            .tableColumns=${table.tableColumns}
            .items=${items}
            .sortBy=${'name'}
            .groupBy=${this.substancesTableConfig.groupBy}
            .filter=${true}
            theme-page
            .contentStickyTop=${this.contentStickyTop}
          ></d-substances-table>`;
      }
    }
    return html``;
  }

  renderContent() {
    if (this.pageView.promoData) {
      return html` <d-promo-section
        theme-page
        .employeesCount=${this.pageView.employeesCount}
        .specialTerms=${this.pageView.specialTerms}
        .promoData=${this.pageView.promoData}
        .features=${features}
        .canUpgrade=${this.writeAccess}
      ></d-promo-section>`;
    }
    return html`
      ${this.renderPageLinks(this.pageView.pageLinks)}
      <d-expansion ?opened=${this.showUploader || this.noSubstances}>
        <div class="${this.progressState}">
          ${this.noSubstances
            ? nothing
            : html`<div class="cancel-upload">
                <d-action @click=${() => this._cancelUpload()}>${t('content.cancel')}</d-action>
              </div>`}

          <div
            class="${this._registerItemsPromptClasses(this.dropTargetHover)}"
            @dragenter=${(e) => this._dragenter(e)}
            @dragleave=${(e) => this._dragleave(e)}
            @dragover=${(e) => this._dragover(e)}
            @drop=${(e) => this._drop(e)}
          >
            ${!this.noSubstances ? nothing : html`<h2>${t('substances.noSubstancesRegistered')}</h2>`}
            <p>${t('substances.dropZonePrompt')}</p>

            <input
              accept=".pdf"
              id="fileInput"
              multiple=""
              @change=${(e) => this._handleFileInputChange(e)}
              type="file"
            />
          </div>
        </div>
      </d-expansion>

      <d-expansion ?opened=${!(this.showUploader || this.noSubstances)}>
        ${this.tables().map((table) => {
          return html`${this.renderSubstancesTable(table)}`;
        })}
      </d-expansion>
    `;
  }

  protected tables(): TableDefinition[] {
    return [
      {
        field: 'substances',
        fieldForLabel: 'organization_substances',
        noHeader: true,
        type: 'substances',
        items: 'substances',
        tableColumns: this.substancesTableColumns(),
      },
      {
        field: 'substancesArchived',
        fieldForLabel: 'organization_substancesArchived',
        type: 'substances',
        items: 'substancesArchived',
        tableColumns: this.substancesTableColumns(),
        excludeFromPfd: true,
      },
    ];
  }

  protected renderHelp(): TemplateResult<1> {
    if (this.pageView.featureStates.substances) {
      return super.renderHelp();
    }
    return html``;
  }

  protected willUpdate(changedProperties: PropertyValues) {
    this.checkForShowUploader();
    // Reload queued items when pageView changes (organization ID may have changed)
    if (changedProperties.has('pageView')) {
      this.loadQueuedItems();
    }
    // Automatically show uploader when list is empty
    if (this.noSubstances && !this.showUploader && !this.uploadInProgress) {
      this.showUploader = true;
    }
  }

  protected override updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);

    // Check if upload just finished (no more pending/queued items)
    if (this.uploadInProgress && this.pageView?.substances) {
      // Check for items still being processed on server
      const hasPendingOnServer = this.pageView.substances.some(
        (s) => s.state === 'pending' || s.state === 'unreadable',
      );
      // Check for items in local queue (queued, uploading, failed, duplicate)
      const hasQueuedItems = this.queuedItems.length > 0;

      // If there are still items, cancel any scheduled reset
      if (hasPendingOnServer || hasQueuedItems) {
        this.groupResetScheduled = false;
        return;
      }

      // Only reset when BOTH server and queue are clear, and not already scheduled
      if (!this.groupResetScheduled) {
        this.groupResetScheduled = true;
        // Reset grouping after delay to allow user to see final state
        setTimeout(() => {
          // Double-check nothing new appeared
          const stillHasPending = this.pageView?.substances?.some(
            (s) => s.state === 'pending' || s.state === 'unreadable',
          );
          const stillHasQueued = this.queuedItems.length > 0;

          if (!stillHasPending && !stillHasQueued) {
            this.uploadInProgress = false;
            const config = cloneDeep(this.substancesTableConfig);
            config.groupBy = ''; // Back to alphabetical
            this.substancesTableConfig = config;
          }
          this.groupResetScheduled = false;
        }, 3000);
      }
    }
  }

  protected renderCustomActions(): TemplateResult | typeof nothing {
    return html`
      <d-action class="action" @click=${() => this._upload()}>${t('substances.upload')}</d-action>
      <d-action href="${this.pageView.pdfHref}" id="print" blank>PDF</d-action>
      ${this.singleUserVersion
        ? nothing
        : html`<d-action class="action" @click=${() => this.onShareContent()}>${t('content.share')}</d-action>`}
    `;
  }

  private substancesTableColumns(): ItemTableColumn[] {
    return [
      {
        name: 'name',
        field: 'name',
        label: t('substances.columnName'),
      },
      {
        name: 'supplier',
        field: 'supplier',
        label: t('substances.columnSupplier'),
        groupOption: true,
        hide: true,
        excludeFromPfd: true,
      },
      {
        name: 'hazardLabels',
        field: 'hazardLabels',
        type: 'hazardLabels',
        label: '',
      },
      {
        name: 'applications',
        field: 'applications',
        label: t('substances.columnApplications'),
      },
      {
        name: 'location',
        field: 'location',
        label: t('substances.columnLocation'),
        groupOption: true,
        hide: true,
        excludeFromPfd: true,
      },
      {
        name: 'risk',
        field: 'risk',
        label: t('substances.columnRisk'),
        groupOption: true,
        excludeFromPfd: true,
      },
      {
        name: 'uploaded',
        field: 'uploaded',
        label: t('substances.columnUploaded'),
        groupOption: true,
        hide: true,
        excludeFromPfd: true,
      },
    ];
  }

  /**
   * Checks for show upload parameter in url and set the property and removes the parameter. Used to initialize upload from other pages
   *
   * @private
   */
  private checkForShowUploader() {
    const query = location.search.slice(1);
    const params = new URLSearchParams(query);
    if (params.has('showSubstancesUploader')) {
      this.showUploader = true;
      history.replaceState({}, '', location.protocol + '//' + location.host + location.pathname);
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-substances-page-content': DSubstancesPageContent;
  }
}
